/* 147j+16bx — In-app User Manual.
 *
 * Plain-English help page for end-users of the AlphaScout + Order Wins
 * dashboard. Mounted as a sidebar tab in both UserDashboard (read-only)
 * and AdminDashboard. Renders the same content as
 * `/app/memory/user_manual.md` (also exposed via /user_manual.md for
 * download) but as JSX so we don't pull in a markdown renderer.
 *
 * Constraint: NO IP / METHODOLOGY LEAKAGE.
 *   - Do NOT mention prompt templates, score weights, model names,
 *     SHA-256 caching, rapidfuzz thresholds, FastAPI/MongoDB, etc.
 *   - Only describe what the user SEES and how to ACT on it.
 */
/* eslint-disable react/no-unescaped-entities, react/jsx-key */
import React from 'react';
import { BookOpen, ListChecks } from 'lucide-react';

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24 mb-10" data-testid={`help-section-${id}`}>
    <h2 className="text-lg font-semibold text-foreground border-b border-border/40 pb-2 mb-4">
      {title}
    </h2>
    <div className="text-sm text-muted-foreground leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const Pill = ({ children, tone = 'default' }) => {
  const tones = {
    default: 'bg-muted/40 text-foreground border-border/60',
    green:   'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    amber:   'bg-amber-500/15 text-amber-300 border-amber-500/30',
    red:     'bg-rose-500/15 text-rose-300 border-rose-500/30',
    blue:    'bg-sky-500/15 text-sky-300 border-sky-500/30',
  };
  return (
    <span className={`inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${tones[tone] || tones.default}`}>
      {children}
    </span>
  );
};

const Table = ({ headers, rows }) => (
  <div className="overflow-x-auto border border-border/40 rounded-md my-3">
    <table className="w-full text-xs">
      <thead className="bg-muted/30 text-foreground">
        <tr>
          {headers.map((h, i) => (
            <th key={i} className="text-left px-3 py-2 font-semibold border-b border-border/40">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-border/30 last:border-0">
            {row.map((cell, j) => (
              <td key={j} className="px-3 py-2 align-top text-muted-foreground">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TOC = () => {
  const items = [
    ['intro',      '1. What this dashboard does'],
    ['nav',        '2. Getting around'],
    ['alphascout', '3. AlphaScout — how to read it'],
    ['orderwins',  '4. Order Wins — how to read it'],
    ['workflow',   '5. Daily workflow'],
    ['glossary',   '6. Glossary'],
    ['faq',        '7. FAQ'],
    ['cheatsheet', '8. One-page cheat sheet'],
  ];
  return (
    <nav
      className="hidden lg:block sticky top-4 self-start w-56 shrink-0 border border-border/40 rounded-md p-3 bg-surface"
      data-testid="help-manual-toc"
    >
      <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-foreground uppercase tracking-wider">
        <ListChecks className="w-3.5 h-3.5 text-primary" />
        Contents
      </div>
      <ul className="space-y-1">
        {items.map(([id, label]) => (
          <li key={id}>
            <a
              href={`#${id}`}
              className="text-xs text-muted-foreground hover:text-primary hover:underline block"
              data-testid={`help-toc-${id}`}
            >
              {label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

const HelpManual = () => {
  return (
    <div className="max-w-6xl mx-auto" data-testid="help-manual-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          <h1 className="text-xl font-semibold text-foreground">User Manual</h1>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Plain-English guide to AlphaScout + Order Wins. Updated Feb 2026.
        </p>
      </div>

      <div className="flex gap-6">
        <TOC />

        {/* Content column */}
        <div className="flex-1 min-w-0">

          <Section id="intro" title="1. What this dashboard does">
            <p>
              This is a <strong className="text-foreground">research & idea-generation</strong> tool,
              not an auto-trader. Every trading day it does two jobs for you:
            </p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>
                <strong className="text-foreground">AlphaScout</strong> maintains three model
                portfolios (Largecap, Mid &amp; Small, Value) and tells you — in plain
                English — what to do with each name today: <Pill tone="green">STRONG BUY</Pill>{' '}
                <Pill tone="blue">BUY · HALF SIZE</Pill> <Pill>HOLD</Pill>{' '}
                <Pill tone="red">SELL</Pill>.
              </li>
              <li>
                <strong className="text-foreground">Order Wins</strong> scans every NSE/BSE
                filing the moment it is published, picks out genuine new business wins
                (contracts, purchase orders, EPC awards, defence orders, etc.) and rates
                each one for short-term tradability and long-term investment value.
              </li>
            </ol>
            <p>
              You decide what to actually buy, sell or hold. The dashboard simply puts
              the day's best ideas in front of you, in priority order, with the
              reasoning attached.
            </p>
          </Section>

          <Section id="nav" title="2. Getting around">
            <p>The left sidebar has four research tabs:</p>
            <Table
              headers={['Tab', "What you'll see"]}
              rows={[
                ['AlphaScout — Largecap',     "Today's large-cap model portfolio"],
                ['AlphaScout — Mid & Small',  'Mid/small-cap "Hunter" portfolio'],
                ['AlphaScout — Value',        '"ValueHunter" — undervalued names'],
                ['Order Wins',                'Live feed of new contract wins, ranked by impact'],
              ]}
            />
            <p>
              Your name and the <strong className="text-foreground">Logout</strong> button are
              always pinned at the bottom of the sidebar. The small{' '}
              <strong className="text-foreground">Tour</strong> button at the top right replays
              the welcome walkthrough.
            </p>
            <p>
              Data refreshes automatically before market open and through the session.
              You do <strong className="text-foreground">not</strong> need to click "refresh" —
              if you see a card, it is current.
            </p>
          </Section>

          <Section id="alphascout" title="3. AlphaScout — how to read it">
            <h3 className="text-sm font-semibold text-foreground mt-2">3.1 The Verdict Badge</h3>
            <p>
              Each stock card carries one badge. Treat the badge as the{' '}
              <strong className="text-foreground">action instruction</strong> for today.
            </p>
            <Table
              headers={['Badge', 'Meaning', 'Typical action']}
              rows={[
                [<Pill tone="green">STRONG BUY</Pill>,    'Highest-conviction pick. Multiple signals are pointing the same way.', 'Take a full-size position.'],
                [<Pill tone="blue">BUY · HALF SIZE</Pill>, 'The model likes it, but conviction is moderate or the setup is early.', 'Take half your normal position size.'],
                [<Pill>HOLD</Pill>,                       "Already in the book and still working. Don't add, don't trim.",         'Sit tight.'],
                [<Pill tone="red">SELL</Pill>,             'The thesis has broken or a better idea has replaced it.',              'Exit on the next reasonable opportunity.'],
              ]}
            />
            <p className="italic">
              If you are short on time, just read the badge. Everything else is detail.
            </p>

            <h3 className="text-sm font-semibold text-foreground mt-5">3.2 The score chips</h3>
            <p>
              Under each badge you may see something like{' '}
              <span className="font-mono text-foreground">S-7 · L-2 · CONF 78%</span>.
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><span className="font-mono text-foreground">S-#</span> — <strong className="text-foreground">Short-term</strong> read on the next few weeks, on a 0–10 scale.</li>
              <li><span className="font-mono text-foreground">L-#</span> — <strong className="text-foreground">Long-term</strong> read on the next few quarters, on a 0–10 scale.</li>
              <li><span className="font-mono text-foreground">CONF #%</span> — <strong className="text-foreground">Confidence</strong>. How sure the model is about its own call. Below 50% means "interesting but unproven" — size down accordingly.</li>
            </ul>
            <p>
              A common pattern is high-S, low-L (a clean trade, not an investment) or
              low-S, high-L (a compounder you should accumulate slowly, not chase). Use
              both numbers together — they tell different stories.
            </p>

            <h3 className="text-sm font-semibold text-foreground mt-5">3.3 Best &amp; Worst Performer</h3>
            <p>
              At the top of each AlphaScout tab there are two cards: the biggest winner
              and the biggest loser since the portfolio bought them. Both{' '}
              <strong className="text-foreground">include closed positions</strong> — the
              dashboard cannot hide its mistakes. That is the honest scoreboard.
            </p>

            <h3 className="text-sm font-semibold text-foreground mt-5">3.4 The "Smart-money trend" pill</h3>
            <p>
              Some cards show a pill labelled <Pill tone="green">Bullish</Pill>{' '}
              <Pill>Neutral</Pill> <Pill tone="red">Bearish</Pill>. Read it as:{' '}
              <em>"Is institutional money currently leaning into this name or away from
              it?"</em> When it disagrees with the verdict badge, scale your size down or
              wait one more day.
            </p>

            <h3 className="text-sm font-semibold text-foreground mt-5">3.5 Sizing — what "HALF SIZE" really means</h3>
            <p>If your normal position is, say, <strong className="text-foreground">2% of capital</strong>:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><Pill tone="green">STRONG BUY</Pill> → take the full 2%</li>
              <li><Pill tone="blue">BUY · HALF SIZE</Pill> → take ~1%</li>
              <li><Pill>HOLD</Pill> → leave the existing position alone</li>
              <li><Pill tone="red">SELL</Pill> → exit the position</li>
            </ul>
            <p>You can adjust the absolute number — the ratio is what matters.</p>
          </Section>

          <Section id="orderwins" title="4. Order Wins — how to read it">
            <p>
              This tab shows every new business win filed by listed Indian companies,
              automatically extracted from NSE and BSE corporate announcements.
            </p>

            <h3 className="text-sm font-semibold text-foreground mt-2">4.1 The order card</h3>
            <p>Each row gives you, at a glance:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Company &amp; exchange (NSE / BSE)</li>
              <li>Order value in ₹ crore</li>
              <li>Customer (e.g. Indian Railways, Ministry of Defence, a private client) — when disclosed</li>
              <li>Sector chip</li>
              <li>S-score, L-score, EXTR %, VERD % — see 4.2 and 4.4 below</li>
              <li>An optional <Pill tone="green">Smart Money</Pill> chip</li>
              <li>A <strong className="text-foreground">Deep Analysis</strong> expander with the full write-up</li>
            </ul>

            <h3 className="text-sm font-semibold text-foreground mt-5">4.2 Short vs Long score</h3>
            <Table
              headers={['Score', 'Question it answers']}
              rows={[
                [<span className="font-mono text-foreground">S-score (0–10)</span>, 'Will this filing move the stock in the next few weeks?'],
                [<span className="font-mono text-foreground">L-score (0–10)</span>, "Does this win change the company's earnings trajectory for the next year or two?"],
              ]}
            />
            <p>
              A government-tender win at fair value can easily score S-3 / L-8 — boring
              trade today, structural compounder over time. A surprise large defence
              order can score S-9 / L-6 — fast move now plus durable backlog growth.
            </p>

            <h3 className="text-sm font-semibold text-foreground mt-5">4.3 "Smart Money" chip</h3>
            <p>
              A green <Pill tone="green">Smart Money</Pill> chip means recent{' '}
              <strong className="text-foreground">institutional bulk-deal activity</strong>{' '}
              has been spotted in that name (mutual funds, FIIs, large HNIs buying or
              selling sizeable blocks). It is an independent confirmation signal — when
              smart money agrees with the order win, the setup is materially stronger.
              Absence of the chip is not a negative; it just means there was no notable
              bulk deal recently.
            </p>

            <h3 className="text-sm font-semibold text-foreground mt-5">4.4 Confidence — when to trust the row</h3>
            <p>
              The Confidence column shows <strong className="text-foreground">two</strong>{' '}
              small numbers, stacked on top of each other:
            </p>
            <Table
              headers={['Label', 'What it measures', 'How to read it']}
              rows={[
                [
                  <span className="font-mono text-foreground">EXTR %</span>,
                  <>How cleanly the <strong className="text-foreground">facts</strong> (order value, customer, order type) were read off the filing.</>,
                  <>High = the numbers on the row are reliable. Low = the filing was vague and the numbers may need a sanity-check against the original PDF.</>,
                ],
                [
                  <span className="font-mono text-foreground">VERD %</span>,
                  <>How sure the model is about the <strong className="text-foreground">Trader / Investor verdict</strong> (BUY / HOLD / SKIP) it picked.</>,
                  <>High = a strong, well-supported call. Low = mixed signals — size down or wait.</>,
                ],
              ]}
            />
            <p>
              On Minor rows you will often see <span className="font-mono text-foreground">EXTR 100%</span>{' '}
              and <span className="font-mono text-foreground">VERD 45% <em>skipped</em></span>. That is
              by design: the facts were read perfectly, but the full verdict
              calculation was deliberately skipped for this row (see 4.6 below) —
              the 45% is a placeholder, not a real call.
            </p>
            <Table
              headers={['Confidence band', 'How to treat it']}
              rows={[
                ['80 – 100%', 'Trust as-is. The signal is clean.'],
                ['60 – 80%',  'Read Deep Analysis before trading.'],
                ['Below 60%', 'Treat as a heads-up only — verify the original filing.'],
              ]}
            />

            <h3 className="text-sm font-semibold text-foreground mt-5">4.5 Deep Analysis — Trader vs Investor</h3>
            <p>The Deep Analysis section opens to reveal:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong className="text-foreground">Trader verdict</strong> — BUY / HOLD / SKIP, focused on the next 1–4 weeks of price action.</li>
              <li><strong className="text-foreground">Investor verdict</strong> — BUY / HOLD / SKIP, focused on the next 4–8 quarters of fundamentals.</li>
              <li>A short, plain-English <strong className="text-foreground">why</strong> — the rationale behind both calls.</li>
            </ul>
            <p>
              It is normal — and informative — for the two verdicts to disagree.
              Example: <em>"Trader: SKIP (already up 8% on the news). Investor: BUY
              (backlog just expanded 30%)."</em> That is exactly the edge this tool is
              meant to surface.
            </p>

            <h3 className="text-sm font-semibold text-foreground mt-5">4.6 Red-Flag Inspector — the "is this deal fishy?" check</h3>
            <p>
              On the biggest orders (typically <Pill tone="green">MAJOR</Pill> rows with a
              BUY verdict), you may see a rose-bordered{' '}
              <Pill tone="red">⚠ Red-Flag Inspector</Pill> panel below the verdict.
              This is a completely separate layer from the BUY/HOLD/SKIP call — its
              job is to answer <em>"is there anything sketchy about this specific
              order that a headline reader would miss?"</em>
            </p>
            <p>
              It looks for things like:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong className="text-foreground">Related-party customer</strong> — order is with a group / promoter-linked company (self-dealing).</li>
              <li><strong className="text-foreground">Replaces a cancelled contract</strong> — a "new win" that quietly makes up for something that fell through.</li>
              <li><strong className="text-foreground">Circular revenue</strong> — customer is also a supplier / investor in the company.</li>
              <li><strong className="text-foreground">Hedging language</strong> — filing uses words like "subject to conditions", "letter of intent", "MoU only".</li>
              <li><strong className="text-foreground">Order &gt; market cap</strong> — a single contract worth more than the entire company.</li>
              <li><strong className="text-foreground">Order &gt; 2× revenue</strong> — commitment spans multiple years, execution/working-capital risk.</li>
              <li><strong className="text-foreground">Same-day cancellation</strong> — a sister cancellation was filed within ±3 days.</li>
            </ul>
            <p>
              A <strong className="text-foreground">Score /10</strong> summarises the
              overall fishiness. Chips are colour-coded:
              <Pill tone="red">HIGH</Pill> · <Pill tone="amber">WARN</Pill> ·{' '}
              <Pill tone="blue">INFO</Pill>. The BUY is still valid — the panel is
              just telling you what to double-check before you size up.
            </p>

            <h3 className="text-sm font-semibold text-foreground mt-5">4.6.1 "Low confidence" — this is NOT a red flag</h3>
            <p>
              You may occasionally see a subtle inline note like{' '}
              <em>"ℹ Low confidence · 75% — AI is unsure of its own extraction."</em>
              This is <strong className="text-foreground">not</strong> a warning about
              the deal itself. It simply means the AI that read the PDF gave itself a
              75% confidence score on the numbers it pulled out (value, customer, order
              type) — below its own 80% comfort bar.
            </p>
            <p>
              Read it as: <em>"trust the direction but sanity-check the exact numbers
              against the original filing."</em> When "Low confidence" is the only
              thing showing, we deliberately render it as a quiet grey line instead of
              a big red-bordered card, so it doesn't look scarier than it is. If it
              ever stacks with a <strong className="text-foreground">real</strong>{' '}
              red flag (related-party, hedging language, cancellation), the full rose
              panel comes back — that's the case where the combination genuinely
              matters.
            </p>

            <h4 className="text-[13px] font-semibold text-foreground mt-3">Confidence benchmark (what the % actually means)</h4>
            <p>
              You'll see TWO confidence chips throughout the app:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>
                <strong className="text-foreground">EXTR %</strong> — extraction
                confidence. <em>"How sure am I I read the number right?"</em>
              </li>
              <li>
                <strong className="text-foreground">VERD %</strong> (also shown as{' '}
                <strong className="text-foreground">CONF %</strong> in the drawer) —
                verdict confidence. <em>"How sure am I about the BUY/HOLD/SKIP call?"</em>
              </li>
            </ul>
            <p>Both use the same benchmark ladder:</p>
            <div className="border border-border/40 rounded overflow-hidden my-2">
              <table className="w-full text-[12px]">
                <thead className="bg-muted/30 text-muted-foreground uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-semibold w-24">Range</th>
                    <th className="text-left px-3 py-1.5 font-semibold w-32">Signal</th>
                    <th className="text-left px-3 py-1.5 font-semibold">What to do</th>
                  </tr>
                </thead>
                <tbody className="text-foreground">
                  <tr className="border-t border-border/30">
                    <td className="px-3 py-1.5 font-mono text-emerald-300">≥ 90%</td>
                    <td className="px-3 py-1.5"><Pill tone="green">HIGH</Pill></td>
                    <td className="px-3 py-1.5 text-muted-foreground">Trust the call as-is. No manual check needed.</td>
                  </tr>
                  <tr className="border-t border-border/30">
                    <td className="px-3 py-1.5 font-mono text-zinc-300">80 – 89%</td>
                    <td className="px-3 py-1.5"><Pill tone="blue">SOLID</Pill></td>
                    <td className="px-3 py-1.5 text-muted-foreground">Good enough. No inline flag will fire.</td>
                  </tr>
                  <tr className="border-t border-border/30">
                    <td className="px-3 py-1.5 font-mono text-amber-300">75 – 79%</td>
                    <td className="px-3 py-1.5"><Pill tone="amber">BORDERLINE</Pill></td>
                    <td className="px-3 py-1.5 text-muted-foreground">Glance at Deep Analysis + constituent filings before sizing up.</td>
                  </tr>
                  <tr className="border-t border-border/30">
                    <td className="px-3 py-1.5 font-mono text-rose-300">&lt; 75%</td>
                    <td className="px-3 py-1.5"><Pill tone="red">HEDGING</Pill></td>
                    <td className="px-3 py-1.5 text-muted-foreground">Open the filing PDF and sanity-check the numbers yourself.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Anything below 80% also triggers the inline{' '}
              <em>"Low confidence · X%"</em> nudge shown above.
            </p>

            <h3 className="text-sm font-semibold text-foreground mt-5">4.7 Major vs Minor orders — why some rows have less detail</h3>
            <p>
              Not every order moves a stock. To keep your attention on what actually
              matters, Order Wins classifies each filing into one of three buckets:
            </p>
            <Table
              headers={['Bucket', 'What qualifies', 'What you see']}
              rows={[
                [
                  <Pill tone="green">MAJOR</Pill>,
                  "Order ≥ ~5% of revenue · OR · ≥ 2% of market cap · OR · from a Tier-1 customer (Defence, PSU, large conglomerate) · OR · a long-tenure concession / PPP / DBFOT contract",
                  "Full Deep Analysis — Trader verdict, Investor verdict, thesis, bull/bear cases, risk flags",
                ],
                [
                  <Pill tone="amber">MINOR</Pill>,
                  "Passes the ≥ ₹1 Cr filter but doesn't clear any of the Major bars above",
                  <>A short <Pill>LOW IMPACT</Pill> note. Treat the row as order-book accretion, not a re-rating catalyst.</>,
                ],
                [
                  <Pill>TRIVIAL</Pill>,
                  "Sub-₹1 Cr AND under 0.5% of market cap",
                  "Filtered out — the row never reaches the feed.",
                ],
              ]}
            />
            <p>
              <strong className="text-foreground">Rule of thumb:</strong> scan the
              green Major rows first. Yellow Minor rows are reference-only — useful to
              spot momentum (10 small orders in a week from one company is a{' '}
              <em>cluster</em> worth noticing), but you should not trade off a Minor
              row alone.
            </p>
            <p>
              If you ever want the full Deep Analysis on a Minor row, ask your
              administrator — there's an admin override to promote any row to Major
              treatment.
            </p>

            <h3 className="text-sm font-semibold text-foreground mt-5">4.8 Sorting &amp; filtering</h3>
            <p>
              Order Wins is ranked by combined impact by default (a blend of S, L, CONF,
              order value and freshness). You can re-sort by any column header, and
              filter by sector, exchange or date if the volume gets noisy on heavy news
              days.
            </p>
          </Section>

          <Section id="workflow" title="5. Daily workflow">
            <h3 className="text-sm font-semibold text-foreground">Morning (before 9:15 AM)</h3>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Open <strong className="text-foreground">AlphaScout — Largecap</strong>. Scan the Verdict Badges.</li>
              <li>Note any new <Pill tone="green">STRONG BUY</Pill> or new <Pill tone="red">SELL</Pill> vs yesterday.</li>
              <li>Open <strong className="text-foreground">Order Wins</strong>. Scan the top 5 rows.</li>
              <li>Earmark 1–3 ideas for the day. Decide your size from the badge.</li>
            </ol>

            <h3 className="text-sm font-semibold text-foreground mt-4">Through the day</h3>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Keep <strong className="text-foreground">Order Wins</strong> open — genuinely large new contracts often hit between 10:30 AM and 2:30 PM. The page auto-refreshes.</li>
              <li>Don't chase parabolic moves. If a name is already up 5%+ on the filing, prefer the Investor verdict.</li>
            </ul>

            <h3 className="text-sm font-semibold text-foreground mt-4">Evening (after 3:30 PM)</h3>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Quick scan of Best &amp; Worst Performer cards — that is the honest scoreboard.</li>
              <li>Review any SELL badges that appeared during the day so you have a clear exit plan for tomorrow's open.</li>
            </ul>
          </Section>

          <Section id="glossary" title="6. Glossary">
            <Table
              headers={['Term', 'Meaning']}
              rows={[
                ['AlphaScout',        'Daily-refreshed model portfolio engine (Largecap, Mid & Small, Value).'],
                ['Order Wins',        'Corporate-filing scanner that picks up new business wins.'],
                ['Verdict Badge',     'Single-line action instruction on each stock card.'],
                ['S-score',           '0–10 reading of short-term impact (next few weeks).'],
                ['L-score',           '0–10 reading of long-term impact (next few quarters).'],
                ['CONF %',            "Confidence in the model's own read on AlphaScout. Size down when low."],
                ['EXTR %',            'Order Wins: how cleanly the facts (value, customer, order type) were read off the filing.'],
                ['VERD %',            'Order Wins: how sure the model is about the Trader/Investor verdict it picked. "skipped" means it was a placeholder on a Minor row.'],
                ['Smart-money trend', '"Bullish / Neutral / Bearish" pill on AlphaScout cards.'],
                ['Smart Money chip',  'Green chip on Order Wins rows — recent institutional bulk-deal activity.'],
                ['Deep Analysis',     'Expandable section with Trader + Investor verdicts and rationale.'],
                ['Red-Flag Inspector','Rose-bordered panel on Order Wins rows that flags fishy contracts (related-party, hedging language, cancellations, oversized-vs-revenue, etc.). Separate layer from the BUY/HOLD/SKIP verdict.'],
                ['Low confidence',   'Grey inline note (not a red flag). The AI is asking you to double-check the extracted numbers against the original filing.'],
                ['Trader verdict',    'BUY / HOLD / SKIP for the next 1–4 weeks.'],
                ['Investor verdict',  'BUY / HOLD / SKIP for the next 4–8 quarters.'],
                ['Major order',       "Order Wins row that earns a full Deep Analysis — either ≥ 5% of revenue, ≥ 2% of market cap, from a Tier-1 customer, or a multi-year concession contract."],
                ['Minor order',       "Order Wins row that meets the ₹1 Cr bar but isn't large enough to move the stock — gets a short 'LOW IMPACT' note instead of a full verdict."],
                ['Trivial order',     'Sub-₹1 Cr and < 0.5% of market cap. Filtered out entirely — never appears in the feed.'],
                ['Best/Worst Performer', 'Top winner / loser in the portfolio (includes closed positions).'],
                ['HALF SIZE',         'Take half of your normal position size.'],
                ['STRONG BUY',        'Highest-conviction full-size pick.'],
                ['HOLD',              'Keep what you have. Do not add. Do not trim.'],
                ['SELL',              'Exit the position on the next reasonable opportunity.'],
              ]}
            />
          </Section>

          <Section id="faq" title="7. FAQ">
            <div className="space-y-4">
              <div>
                <p className="text-foreground font-medium">Q. Why is Order Wins quiet on some mornings?</p>
                <p>Corporate filings come in waves. Some mornings have 30 fresh filings; some have 3. The scanner is running normally either way.</p>
              </div>
              <div>
                <p className="text-foreground font-medium">Q. The price already moved before I saw the badge — should I chase?</p>
                <p>Usually no. Prefer the Investor verdict on Order Wins, or wait for the next dashboard refresh on AlphaScout — both are designed to hand you the <em>next</em> opportunity, not the one that already ran.</p>
              </div>
              <div>
                <p className="text-foreground font-medium">Q. AlphaScout said SELL but I'm still in profit. Should I exit?</p>
                <p>A SELL means "the model no longer wants to own this name" — not "the stock will fall tomorrow". Treat it as a portfolio decision: free up the capital for the next STRONG BUY.</p>
              </div>
              <div>
                <p className="text-foreground font-medium">Q. Why does this Order Wins row only show "LOW IMPACT" and no full analysis?</p>
                <p>That's a <strong className="text-foreground">Minor order</strong> (see Section 4.6). The order met the ₹1 Cr filter but didn't clear the Major bar (≥ 5% of revenue, ≥ 2% of market cap, Tier-1 customer, or a long-tenure concession). The row is shown for completeness, but it's order-book accretion — not a re-rating catalyst. Don't trade off it alone.</p>
              </div>
              <div>
                <p className="text-foreground font-medium">Q. The row shows EXTR 100% but VERD 45% with "skipped" next to it. Is something broken?</p>
                <p>No — that's a Minor row. The facts on the row were read cleanly (EXTR 100%) but the full verdict was deliberately skipped because the order isn't large enough to materially move the stock. The 45% is a placeholder, not a real call. Treat the row as reference-only.</p>
              </div>
              <div>
                <p className="text-foreground font-medium">Q. Can the model be wrong?</p>
                <p>Yes. Look at the Worst Performer card every evening — that is the dashboard's own honesty check. The edge comes from the <em>average</em> of many calls, not any single one.</p>
              </div>
              <div>
                <p className="text-foreground font-medium">Q. How do I know the data is fresh?</p>
                <p>The cards carry a timestamp. The badge at the top of each tab also shows the last refresh time. If it is more than a few hours old during market hours, ping your administrator.</p>
              </div>
            </div>
          </Section>

          <Section id="cheatsheet" title="8. One-page cheat sheet">
            <pre className="bg-muted/30 border border-border/40 rounded-md p-4 text-[11px] font-mono leading-relaxed text-foreground overflow-x-auto whitespace-pre">
{`AlphaScout card
  ┌──────────────────────────────────────────────┐
  │  TICKER                       [STRONG BUY]   │   ← action
  │  S-7 · L-6 · CONF 82%                        │   ← scores
  │  Smart-money trend: Bullish                  │   ← flow check
  │  Entry / Target / Stop                       │
  └──────────────────────────────────────────────┘

Order Wins row
  ┌──────────────────────────────────────────────┐
  │  COMPANY   NSE    ₹ 420 Cr   Defence   ●●●●  │
  │  S-8 · L-7   EXTR 92% / VERD 81%             │
  │                            [Smart Money]     │
  │  ▸ Deep Analysis                             │
  │       Trader: BUY (1–4 wks)                  │
  │       Investor: BUY (4–8 qtrs)               │
  │       Why: <plain-English rationale>         │
  └──────────────────────────────────────────────┘

Size guide
  STRONG BUY   →  full size
  BUY · HALF   →  half size
  HOLD         →  no action
  SELL         →  exit`}
            </pre>
          </Section>

          <p className="text-xs text-muted-foreground italic pt-4 border-t border-border/40">
            Questions, bug reports or feature requests? Speak to your account
            administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HelpManual;
